/**
 * @file        env.js
 *              Module to return information about the DCP host environment
 *
 * @note        Maintenance note - this module used by debugging() during other modules'
 *              initialization - this means we should avoid debugging() calls during our
 *              own init.
 *
 * @author      Wes Garland, wes@kingsds.network
 * @date        Oct 2019
 */
const { hasNativeRequire } = require('./dcp-env-capabilities')
const { requireNative } = require('dcp/dcp-client/webpack-native-bridge');
const bundleScript = typeof document === 'object' ? document.currentScript : false;

exports.platformList = [
  'unknown', // must be first, see below
  "nodejs",
  "pythonmonkey",
  "bravojs",
  "cjs2-generic",
  "vanilla-web"
];

var platform = exports.platformList[0];

exports.browserPlatformList = [
  "bravojs",
  "vanilla-web"
];

exports.referencedTimerPlatformList = [
  "nodejs",
  "pythonmonkey"
];

Object.defineProperty(exports, 'platform', {
  get: function dcpEnv$$platform_getter() {
    /** On initial load, the platform is 'unknown', unless you either
     *  1. are in a CJS env, in which case we use requireNative('dcp/env-native').platform, or
     *  2. this script (webpack bundle) was loaded from a script tag whose dcp-env attribute was set, or
     *  3. we detect PythonMonkey
     */
    if (platform === 'unknown')
    {
      if (globalThis.python?.pythonMonkey)
        exports.setPlatform('pythonmonkey');
      else if (hasNativeRequire)
      {
        try
        {
          // we inject this module in dcp-client
          exports.setPlatform(requireNative('dcp/env-native').platform)
        }
        catch (e)
        {
          console.warn(e);
        }
      }
      else if (bundleScript)
      {
        exports.setPlatform((platform = bundleScript.getAttribute('dcp-env') || platform))
      }
    }
    Object.defineProperty(exports, 'platform', { value: platform, enumerable: true, configurable: true });
    exports.platform = platform;
    return platform
  },
  enumerable: true,
  configurable: true,
});

Object.defineProperty(exports, 'isBrowserPlatform', {
  get: function dcpEnv$$isBrowserPlatform_getter() {
    // use exports.platform, so if it is called before platform is initialized, 
    // that will be done rather than using an uninitialized value
    const value = exports.browserPlatformList.indexOf(exports.platform) !== -1;
    Object.defineProperty(exports, 'isBrowserPlatform', { value, enumerable: true, configurable: true });
    return value
  },
  enumerable: true,
  configurable: true,
});

Object.defineProperty(exports, 'isBrowserChrome', {
  get: function dcpEnv$$isBrowserChrome_getter() {
    let isBrowser = exports.browserPlatformList.indexOf(exports.platform) !== -1;
    if(isBrowser){
      return !!window.navigator.userAgent.match(/Chrome\//g);
    } 
  },
  enumerable: true,
});

Object.defineProperty(exports, 'isReferencedTimerPlatform', {
  get: function dcpEnv$$isReferencedTimerPlatform_getter() {
    // use exports.platform, so if it is called before platform is initialized, 
    // that will be done rather than using an uninitialized value
    return exports.referencedTimerPlatformList.indexOf(exports.platform) !== -1;
  },
  enumerable: true,
});

exports.setPlatform = function setPlatform(newPlatform)
{
  if (exports.platformList.indexOf(newPlatform) === -1)
    throw new Error('Invalid platform: ' + newPlatform);

  platform = newPlatform;
  exports.setPlatformDebugStack = new Error('\rPlatform set').stack;  /* Breadcrumbs in case platform gets set wrong by somebody */

  exports.initPolyfills(platform);
  return platform;
}

exports.setPlatformDebugStack = new Error('Never set');

/** Return a value based on the current platform
 *
 *  @param      cases           key-value-pair object where the key is the
 *                              name of the platform and the value is the value
 *                              to return.  An extra key, "default", is supported
 *                              in case the current platform does not match any
 *                              other key/
 *  @throws     {Error}         when the current platform is completely unhandled
 *                              (implies default key not provided)
 */
exports.switch = function env$switch(cases)
{
  for (let prop in cases)
  {
    if (!cases.hasOwnProperty(prop))
      continue;

    if (prop === exports.platform)
      return cases[prop];
  }

  if (exports.platform === 'unknown')
  {
    const stack = new Error().stack;

    if (stack)
      console.warn('Warning: platform unknown in env module at', stack.split('\n')[2].replace(/^.*\(/, '').replace(/\) *$/, ''))
    else
      console.warn('Warning: platform unknown in env module');
  }

  if (cases.hasOwnProperty('default'))
    return cases.default

  throw new Error(`Platform ${exports.platform} not handled in cases object`);
}

/**
 * Add global symbols normally present in browser env but not normally
 * present in node env
 */
function addNodePolyfills()
{
  const process = requireNative('process');
  exports.global.btoa = requireNative('btoa');
  exports.global.performance = requireNative('perf_hooks').performance;
  exports.programFilename = requireNative.main?.filename;

  /**
   * Process.emit wrapper which eliminates certain annoying warnings. The underlying implementation is
   * memoized on the process object to avoid going through this check twice when using dcp-client, which
   * effectively calls this function twice due to two-stage bundle initialization /wg jun 2025
   */
  if (!process.__dcp__processEmitImpl)
  {
    process.__dcp__processEmitImpl = process.emit;
    process.emit = (...args) => {
      if (args[0] === 'warning' && !process.env.DCP_DEBUG && !process.env.DEBUG &&
          args[1].message === 'ExperimentalWarning: WASI is an experimental feature and might change at any time')
        return;
      process.__dcp__processEmitImpl.apply(process, args);
    };
  }
}

function addPythonMonkeyPolyfills()
{
  const start = Date.now();
  const python = globalThis.python;

  globalThis.performance = {
    now: function pm$$performance$now() {
      return Date.now() - start;
    }
  }

  python.exec(`
import pythonmonkey
import sys
import os
import inspect
`);

  process.argv0 = python.eval('sys.executable');
  process.argv = [ process.argv0 ].concat(python.eval('sys.argv'));
  process.exit = function pm$$processExit(exitCode) {
    if (arguments.length === 0)
      exitCode = process.exitCode;
    if (typeof exitCode !== 'number')
      exitCode = !exitCode ? 0 : parseInt(exitCode) || 1;
    python.eval('lambda x: exit(int(x))')(exitCode & 255);
  };

  const fsBasic = require('./fs-basic');
  fsBasic.writeFileAtomic = function writeFileAtomic(filename, contents)
  {
    /* todo: implement in python, make use tempnam, cleanup atexit */
    const tempFilename = filename + '-' + Math.random().toString(16).slice(2);
    fsBasic.writeFile(tempFilename, contents);
    python.eval('__import__("os").replace')(tempFilename, filename);
  }

  exports.dcpClientBundleFilename = python.eval('__file__');
  exports.programFilename = python.eval('inspect.stack()[1].filename');
}

/**
 * Set up polyfills, which we use to set our base capabilities.
 */
exports.initPolyfills = function env$initPolyfills(platform) {
  if (platform === 'nodejs')
  {
    exports.global = global;
    addNodePolyfills();
  }
  else if (platform === 'pythonmonkey')
  {
    exports.global = globalThis;
    addPythonMonkeyPolyfills();
  }
  else if (typeof globalThis !== 'undefined')
    exports.global = globalThis;
  else if (typeof window !== 'undefined')
    exports.global = window;
  else if (typeof global !== 'undefined')
    exports.global = global;
  else if (typeof self !== 'undefined')
      exports.global = self;
  if (!exports.global)
    throw new Error('Could not locate global object');
}

/**
 * Get an environment variable. On NodeJS, this is the usual process environment. On the web, the
 * process environment is emulated via the contents of window.sessionStorage.env
 *
 * @param {string}      which            the environment variable whose value we want to return
 * @returns {string}
 */
exports.getenv = function dcpEnv$$getenv(which)
{
  if (exports.isBrowserPlatform)
  {
    try
    {
      let env = {};
      let envJSON = window.sessionStorage.getItem('env');
      if (envJSON)
        Object.assign(env, JSON.parse(envJSON));

      envJSON = window.localStorage.getItem('env');
      if (envJSON)
        Object.assign(env, JSON.parse(envJSON));

      return env.hasOwnProperty(which) ? env[which] : '';
    }
    catch(error)
    {
      console.error(`Could not get environment variable ${which}; ${error.message}`, error.stack || '');
    }
  }
  else if (exports.platform === 'nodejs')
  {
    const { requireNative } = require('dcp/dcp-client/webpack-native-bridge');
    return requireNative('process').env[which];
  }
  else if (exports.platform === 'pythonmonkey')
  {
    return python.getenv(which);
  }
  else
  {
    console.error(`Could not locate environment on platform ${exports.platform}`);
  }
  return undefined;
}

/**
 * Get an environment variable. On NodeJS, this is the usual process environment. On the web, the
 * process environment is emulated via the contents of window.sessionStorage.env
 *
 * @param {string}      which            the environment variable whose value we want to set
 * @param {any}         value            the value we want to set it to - coerced to string before setting;
 * @param {boolean}     persist          truey to use session storage instead of local storage 
 *                                       on browser; unused for other platforms
 * @returns {string}
 */
exports.setenv = function dcpEnv$$setenv(which, value, persist)
{
  value = String(value);
  
  try
  {
    if (exports.platform === 'nodejs')
    {
      const process = requireNative('process');
      process.env[which] = value;
    }
    else if (exports.platform === 'pythonmonkey')
    {
      const scope = { which, value };
      python.exec(`
import os
os.environ[which] = value
`, scope);
    }
    else if (exports.isBrowserPlatform)
    {
      let envJSON = persist ? window.sessionStorage.getItem('env') : window.localStorage.getItem('env');
      let env = envJSON ? JSON.parse(envJSON) : {};

      env[which] = value;
      (persist ? window.sessionStorage : window.localStorage).setItem('env', JSON.stringify(env));
    }
    else
    {
      console.error(`Could not set environment variable ${which}`);
    }
  }
  catch(error)
  {
    console.error(`Could not set environment variable ${which}; ${error.message}`, error.stack || '');
  }
}


/**
 * @returns {string|undefined} best guess at the process path
 */
exports.getProcessPath = function dcpEnv$$getProcessPath()
{
  const env = exports;
  if (env.isBrowserPlatform)
    return globalThis.location.pathname;
  else if (env.platform === 'nodejs')
    return requireNative('process').argv[1] || 'node';
  else if (env.platform === 'pythonmonkey')
    return globalThis.python.eval('__import__("__main__")')['__file__'];
}

/**
 * @returns best guess at name of process
 */
exports.getProcessName = function dcpEnv$$getProcessName()
{
  const env = exports;
  if (env.isBrowserPlatform)
    return document.title;

  const processPath = exports.getProcessPath();
  if (!processPath)
    return '<unknown process>';

  const fsBasic = require('./fs-basic'); /* must late load */
  return fsBasic.basename(processPath).replace(/\.[a-z]+$/, '');
}
